import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const PrefsContext = createContext(null);

// AU/NZ timezone options
export const TIMEZONES = [
  { id: "auto", label: "Auto (device)" },
  { id: "AEST", label: "AEST (Sydney/Melbourne/Brisbane)", offset: 10 },
  { id: "AEDT", label: "AEDT (Sydney/Melbourne DST)", offset: 11 },
  { id: "ACST", label: "ACST (Adelaide/Darwin)", offset: 9.5 },
  { id: "AWST", label: "AWST (Perth)", offset: 8 },
  { id: "NZST", label: "NZST (New Zealand)", offset: 12 },
  { id: "NZDT", label: "NZDT (New Zealand DST)", offset: 13 },
  { id: "UTC", label: "UTC", offset: 0 },
];

export const STATES = [
  { id: "NSW", label: "NSW", country: "AU" },
  { id: "VIC", label: "VIC", country: "AU" },
  { id: "QLD", label: "QLD", country: "AU" },
  { id: "WA", label: "WA", country: "AU" },
  { id: "SA", label: "SA", country: "AU" },
  { id: "TAS", label: "TAS", country: "AU" },
  { id: "ACT", label: "ACT", country: "AU" },
  { id: "NT", label: "NT", country: "AU" },
  { id: "NZ-NI", label: "NZ North Island", country: "NZ" },
  { id: "NZ-SI", label: "NZ South Island", country: "NZ" },
];

export const THEMES = [
  { id: "tactical", label: "Tactical" },
  { id: "hivis", label: "Hi-Vis" },
  { id: "colorblind", label: "Colorblind" },
];

const SLANG = {
  "Sign In": "G'day, log in",
  "Enlist": "Sign up, mate",
  "Loading": "Hang on, cobber",
};

const defaults = {
  timezone: "auto",
  state_flag: null,
  slang_enabled: false,
  theme: "tactical",
};

export const PrefsProvider = ({ children, user, setUser }) => {
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem("wardogs_prefs");
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch (e) { console.warn("prefs parse failed", e); }
    return defaults;
  });

  // hydrate from user record if authed
  useEffect(() => {
    if (user && (user.timezone || user.state_flag || user.theme || user.slang_enabled !== undefined)) {
      setPrefs((p) => ({
        ...p,
        timezone: user.timezone || p.timezone,
        state_flag: user.state_flag ?? p.state_flag,
        slang_enabled: user.slang_enabled ?? p.slang_enabled,
        theme: user.theme || p.theme,
      }));
    }
  }, [user?.id]); // eslint-disable-line

  useEffect(() => {
    localStorage.setItem("wardogs_prefs", JSON.stringify(prefs));
    document.documentElement.setAttribute("data-theme", prefs.theme);
  }, [prefs]);

  const update = useCallback(async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    if (user) {
      try {
        const { data } = await api.patch("/auth/prefs", patch);
        if (setUser) setUser(data);
      } catch (e) { console.warn("prefs sync failed", e); }
    }
  }, [user, setUser]);

  const formatTime = useCallback((iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    let tz = prefs.timezone;
    if (tz === "auto") {
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    }
    const meta = TIMEZONES.find((t) => t.id === tz);
    if (!meta || meta.offset === undefined) return d.toISOString();
    const local = new Date(d.getTime() + meta.offset * 3600 * 1000);
    const y = local.getUTCFullYear();
    const m = String(local.getUTCMonth() + 1).padStart(2, "0");
    const day = String(local.getUTCDate()).padStart(2, "0");
    const hh = String(local.getUTCHours()).padStart(2, "0");
    const mm = String(local.getUTCMinutes()).padStart(2, "0");
    return `${y}-${m}-${day} ${hh}:${mm} ${tz}`;
  }, [prefs.timezone]);

  const t = useCallback((str) => {
    if (!prefs.slang_enabled) return str;
    return SLANG[str] || str;
  }, [prefs.slang_enabled]);

  return (
    <PrefsContext.Provider value={{ prefs, update, formatTime, t }}>
      {children}
    </PrefsContext.Provider>
  );
};

export const usePrefs = () => useContext(PrefsContext);
